import React, { useState, useEffect, useRef } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountBoxIcon from '@material-ui/icons/AccountBox';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import ChecklistIcon from '@mui/icons-material/Checklist';
import Divider from '@mui/material/Divider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConfluence } from "../components/qeeConfluenceProvider"; 
import { useAuth } from "../context/provider";
import QeeNavigator from '../context/navigator';
import ResponsiveAppBar from '../components/appbar';
import UsrChats from '../components/usrChats';
import UsrChat from '../components/usrChat';

// Custom hook to handle sessionStorage logic
function useSessionStorage(globalSessionKey, initialState, filtersKey = 'filters', selectedChatKey = 'selected-chat') {
    const [state, setState] = useState(() => initStateFromSessionStorage(globalSessionKey, initialState));
    const [filters, setFilters] = useState(() => initStateFromSessionStorage(globalSessionKey, initialState.initialFilter, filtersKey));
    const [selectedChat, setSelectedChat] = useState(() => initStateFromSessionStorage(globalSessionKey, null, selectedChatKey));

    // Update session storage when state, filters, or selected chat change
    useEffect(() => {
        updateSessionStorage(globalSessionKey, 'state', state);
        updateSessionStorage(globalSessionKey, filtersKey, filters);
        if (selectedChat) {
            updateSessionStorage(globalSessionKey, selectedChatKey, selectedChat);
        }
    }, [state, filters, selectedChat]);

    return { state, setState, filters, setFilters, selectedChat, setSelectedChat };
}

const useStyles = makeStyles((theme) => ({
    appBar: {
        overflow: 'hidden',
    },
    content: {
        flexGrow: 1,
        padding: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        height: '90vh',
        overflow: 'hidden',
    },
    emptyContainer: {	
		padding: theme.spacing(4),
	},
    mainContent: {
        display: 'flex',
        flexDirection: 'row',
        height: '90vh',
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        }
    },
    listContainer: {
        flex: 1,
        paddingRight: theme.spacing(1),
        boxSizing: 'border-box',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflow: 'visible',
    },
    chatContainer: {
        flex: 2,
        paddingLeft: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        paddingLeft: theme.spacing(0.5),
        marginTop: 0
    }
}));

const Chats = () => {
    const globalSessionKey = 'chats-global';
    const selectedChatSessionKey = 'selected-chat'; 
    const { setOpenConfluenceDialog } = useConfluence();
    const { setOpenQeeDialog, gstate, setObjEdit } = useAuth();    
    const navigate = useNavigate();
    const location = useLocation();
    
    const { loadHome, loadFacs, loadAccount, loadChats, loadSacs } = QeeNavigator();    

    const initialState = {
        initialFilter: { isP2P: true }, // Initial filter state
        render: 'Chats', // Initial render state
        title: 'Chats', // Initial title
        filterReady: false // Track if filter is set
    };

    // Use the custom hook for managing state, filters, and selected chat
    const { state, setState, filters, setFilters, selectedChat, setSelectedChat } = useSessionStorage(globalSessionKey, initialState);

    const [uiLoading, setUiLoading] = useState(true);
    const [chats, setChats] = useState([]);	
    const [showChatContent, setShowChatContent] = useState(false); // For small screens, show chat or list
    const classes = useStyles();
    const theme = useTheme();
    const isMdUp = useMediaQuery(theme.breakpoints.up('md')); // Detect if screen is larger than md
    const { render, title, filterReady } = state;

    const chatUpdateRef = useRef(null);

    // Merge filters
    const mergeFilters = (localFilter) => {
        return { ...filters, ...localFilter };
    };

    // Handle filters from URL and set up the state
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const filter = {};
        if (params.has('filter')) {
            const filterParams = new URLSearchParams(params.get('filter'));
            filterParams.forEach((value, key) => {
                filter[key] = value;
            });
        }
        const mergedFilters = mergeFilters(filter);
        setFilters(mergedFilters);
        setState(prevState => ({
            ...prevState,
            title: 'Nachrichten',
            filterReady: true // Indicate that the filter is ready
        }));
    }, [location.search]);

    useEffect(() => {
        setUiLoading(false);
		
		// Kill the body overflow    
        try {
            const originalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
            return () => {
              document.body.style.overflow = originalOverflow;
            };    
        } catch (e) {
            console.error('Error set/reset body overflow', e);
        }
    }, []);

    // Handle chat selection
    const handleChatSelect = (chat) => {
        setSelectedChat(chat);
        updateSessionStorage(globalSessionKey, selectedChatSessionKey, chat); // Save selected chat to sessionStorage
        if (!isMdUp) 
			setShowChatContent(true); // On small screens, show the chat content
    };

    // Handle loaded chats
    const handleChatsLoaded = (chats) => {
        setChats(chats);

        // Only select a chat if none is already selected from session storage
        if (!selectedChat) {
            const storedChat = initStateFromSessionStorage(globalSessionKey, null, 'selected-chat');
            if (storedChat) {
                const foundChat = chats.find(c => c.id === storedChat.id);
                if (foundChat) {
                    setSelectedChat(foundChat);
                }
            } else if (chats.length > 0) {
                setSelectedChat(chats[0]); // If no stored chat, select the first chat
            }
        }
    };

    // For small screens, go back to chat list
    const handleBackToChats = () => {
        setShowChatContent(false);
    };

    const handleChatUpdate = () => {
        if (chatUpdateRef.current) {
            chatUpdateRef.current();
        }
    };

    // Function to render chats content for large screens
    const renderLargeScreenLayout = () => {
        return (
            <Box className={classes.mainContent}>
                <Box className={classes.listContainer}>
                    <UsrChats 
                        title={title} 
                        onChatSelect={handleChatSelect} 
                        onChatsLoaded={handleChatsLoaded} 						
                        filters={filters || {}} 
                        onChatUpdate={chatUpdateRef}
                    />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box className={classes.chatContainer}>
                    {selectedChat ? (
                        <UsrChat key={selectedChat.id} chat={selectedChat} onChatUpdate={handleChatUpdate} />
                    ) : (
                        <Typography className={classes.emptyContainer}>Wähle einen Chat zur Ansicht aus!</Typography>
                    )}
                </Box>
            </Box>
        );
    };

    // Function to render chats content for small screens
    const renderSmallScreenLayout = () => {
        return (
            <Box className={classes.mainContent}>
                {showChatContent ? (
                    <Box className={classes.chatContainer}>
                        <IconButton onClick={handleBackToChats}>
                            <ArrowBackIcon />
                        </IconButton>
                        {selectedChat ? (
                            <UsrChat key={selectedChat.id} chat={selectedChat} onChatUpdate={handleChatUpdate} />
                        ) : (
                            <Typography className={classes.emptyContainer}>Wähle einen Chat zur Ansicht aus!</Typography>
                        )}
                    </Box>
                ) : (
                    <Box className={classes.listContainer}>
                        <UsrChats 
                            title={title} 
                            onChatSelect={handleChatSelect} 
                            onChatsLoaded={handleChatsLoaded} 						
                            filters={filters || {}} 
                            onChatUpdate={chatUpdateRef}
                        />
                    </Box>
                )}
            </Box>
        );
    };

    const showConfluenceHome = () => {
        setOpenConfluenceDialog();
    };

    const showVersionInfo = () => {
        setOpenQeeDialog({ open: true, id: 'dlgVersionInfo' });
    };

    const logoutHandler = () => {
        localStorage.removeItem('AuthToken');
        navigate('/login');
    };

    const switchSubpage = () => {
        switch (render) {
            case 'Facs':
                return loadFacs();
            case 'Account':
                return loadAccount();
            case 'Chats':
            default:
                return isMdUp ? renderLargeScreenLayout() : renderSmallScreenLayout();
        }
    };

    const settings = [
        { nm: 'Mein Konto', fct: () => setState({ ...state, render: 'Account' }), ico: <AccountBoxIcon /> },
        { nm: 'Aktivitäten', fct: loadSacs, ico:<ChecklistIcon />},
        { nm: 'Wissensassistent', fct: showConfluenceHome, ico: <HelpOutlinedIcon /> },
        { nm: 'Versionsinfo', fct: showVersionInfo, ico: <PrivacyTipIcon /> },
        { nm: 'Logout', fct: logoutHandler, ico: <ExitToAppIcon /> }
    ];

    return (
        <>
            {uiLoading ? (
                <CircularProgress />
            ) : (
                <ResponsiveAppBar
                    className={classes.appBar}
                    drawerWidth={0}
                    switchSubpage={switchSubpage}
                    pages={[]}
                    settings={settings}
                />
            )}
        </>
    );
};

// Helper functions for session storage
function initStateFromSessionStorage(globalKey, defaultState, subKey = null) {
    const savedState = sessionStorage.getItem(globalKey);
    const parsedState = savedState ? JSON.parse(savedState) : defaultState;

    if (parsedState && subKey) {
        return parsedState[subKey] !== undefined ? parsedState[subKey] : defaultState;
    }

    return parsedState;
}

function updateSessionStorage(globalKey, subKey, value) {
    const savedState = sessionStorage.getItem(globalKey) ? JSON.parse(sessionStorage.getItem(globalKey)) : {};
    savedState[subKey] = value;
    sessionStorage.setItem(globalKey, JSON.stringify(savedState));
}

export default Chats;
